import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import {
  type AuthStoreRootProps,
  fullLogout,
  type OryUserResponse,
  useAuthStore,
} from '@revelio/auth';
import { AuthenticationError, HttpError } from '@revelio/data-access';

import { environment } from '../../environments/environment';

// Session length of 12 hours
const SESSION_VALIDATED_LENGTH = 1000 * 60 * 60 * 12;
const isSessionValidatedDateStale = (
  session_validated_at: string | undefined
) => {
  return (
    session_validated_at &&
    // biome-ignore lint/complexity/useDateNow: migrated from develop
    +new Date() - +new Date(session_validated_at) > SESSION_VALIDATED_LENGTH
  );
};

export const useWhoAmI = (oryState: AuthStoreRootProps['ory']) => {
  const setOry = useAuthStore((state) => state.setOry);
  const setIsAuthenticated = useAuthStore((state) => state.setIsAuthenticated);
  const { data, isLoading, error, refetch } = useQuery<OryUserResponse>({
    queryKey: ['whoami'],
    queryFn: fetchWhoAmI,
    meta: { skipGlobalErrorHandling: true },
  });

  useEffect(() => {
    if (error) {
      console.error(error);
      fullLogout();
    }

    if (data && !isLoading) {
      const oryUpdate: AuthStoreRootProps['ory'] = {
        id: data.identity?.id,
        active: data.active,
        name: data.identity?.traits?.name,
        email: data.identity?.traits?.email,
        username: data.identity?.traits?.username,
        expires_at: data.expires_at,
        authenticated_at: data.authenticated_at,
        session_validated_at: new Date().toISOString(),
        account_created_at: data.identity?.created_at,
      };
      setOry(oryUpdate);

      if (data?.identity?.id) {
        setIsAuthenticated(true);
      }
    }
  }, [isLoading, data, error, setOry, setIsAuthenticated]);

  const isSessionStale = isSessionValidatedDateStale(
    oryState?.session_validated_at
  );

  if (isSessionStale && !isLoading && !error) {
    refetch();
  }

  return { data, isLoading };
};

const fetchWhoAmI = async (): Promise<OryUserResponse> => {
  const response = await fetch(
    `${environment.ORY_KRATOS_URL}/sessions/whoami`,
    { credentials: 'include' }
  );

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new AuthenticationError(
        response.status,
        'Session expired or unauthorized'
      );
    }
    throw new HttpError(
      `Failed to fetch whoami: ${response.statusText}`,
      response.status,
      response.statusText
    );
  }

  return response.json();
};
